import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import {
  Button,
  Card,
  Form,
  Input,
  Container,
  Row,
  Col,
  FormGroup,
  Label,
} from "reactstrap";

import NotificationAlert from "react-notification-alert";
import notificationManager from "managers/notificationManager";

import { staticData, termsLoader } from "managers/staticDataManager";
import { useFormik } from "formik";

import { loginValidationSchema, initalLoginModel } from "../validations";
import { handleAuthenticate, getChannelsForIssuer } from "../callApi";
import {
  authInitialize,
  changeIssuer,
  initializeChannels,
  changeChannel,
} from "store/accountActions";
import { issuerUrls } from "config/endPoints";
import { withLang } from "managers/routeManager";

import DecisionModal from "./DecisionModal";

const Authenticate = () => {
  // Moved both DOM side-effects into a single useEffect with [].
  // Running document.body mutations directly in the render body is an
  // anti-pattern — side effects belong in effects, not during render.
  React.useEffect(() => {
    document.documentElement.classList.remove("nav-open");
    document.body.classList.add("register-page");
    return () => document.body.classList.remove("register-page");
  }, []);

  const dispatch = useDispatch();
  const notificationAlertRef = React.useRef(null);
  const state = useSelector((state) => state);
  const [user, setUser] = useState({});
  const [isBusy, setIsBusy] = useState(false);
  const [isIssuerModal, setIsIssuerModal] = useState(false);
  const [associatedIssuers, setAssociatedIssuers] = useState([]);
  const validationSchema = loginValidationSchema(state);

  const formik = useFormik({
    initialValues: initalLoginModel,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (data) => {
      handleAuthenticate(
        state,
        data,
        setIsBusy,
        handleAuth,
        notificationAlertRef
      );
    },
  });

  const handleAuth = (userData, issuers) => {
    setUser(userData);
    setAssociatedIssuers(issuers);
    if (!issuers || issuers.length === 0) {
      return;
    }

    if (issuers.length === 1) {
      performAuth(userData, issuers[0]);
      return;
    }

    setIsIssuerModal(true);
  };

  const performAuth = (userData, issuer) => {
    dispatch(
      authInitialize({
        ...userData,
        language: state.account?.language || userData.language,
      })
    );

    dispatch(changeIssuer(issuer));
    handlePostAuth(issuer);
    termsLoader(dispatch);
  };

  const handlePostAuth = (issuer) => {
    getChannelsForIssuer(
      state,
      issuer.issuerId,
      setIsBusy,
      setChannels,
      notificationAlertRef
    );
  };

  const setChannels = (data) => {
    if (!data || data.length === 0) {
      notificationManager.error(
        state,
        null,
        notificationAlertRef,
        "VW_ISSUER_NOT_ASSOCIATED"
      );
      return;
    }

    dispatch(initializeChannels(data));
    dispatch(changeChannel({ channelId: data[0].channelId }));
  };

  const cancelModal = () => {
    setIsIssuerModal(false);
    setUser({});
    setIsBusy(false);
    setAssociatedIssuers([]);
  };

  return (
    <React.Fragment>
      <Container>
        <Row>
          <DecisionModal
            state={state}
            isVisible={isIssuerModal}
            isBusy={isBusy}
            setIsBusy={setIsBusy}
            user={user}
            issuers={associatedIssuers}
            handlePostAuth={handlePostAuth}
            onHide={cancelModal}
            alertRef={notificationAlertRef}
          />
          <Col className="ml-auto mr-auto" lg="4">
            <Card className="card-register ml-auto mr-auto">
              <Form className="register-form" onSubmit={formik.handleSubmit}>
                <FormGroup>
                  <Label for="formUsername" style={{ fontWeight: "bold" }}>
                    {staticData.loadTerms(
                      state,
                      "VW_ISSUER_Authenticate_formUsername"
                    )}
                  </Label>
                  <Input
                    id="formUsername"
                    name="username"
                    placholder={staticData.loadTerms(
                      state,
                      "VW_ISSUER_Authenticate_formUsername"
                    )}
                    type="text"
                    autoComplete="username"
                    onChange={formik.handleChange}
                    value={formik.values.username}
                    disabled={isBusy}
                  />
                  <div className="text-danger" style={{ fontWeight: "bold" }}>
                    {formik.errors.username ? formik.errors.username : null}
                  </div>
                </FormGroup>
                <FormGroup>
                  <Label for="formPassword" style={{ fontWeight: "bold" }}>
                    {staticData.loadTerms(
                      state,
                      "VW_ISSUER_Authenticate_formPassword"
                    )}
                  </Label>
                  <Input
                    id="formPassword"
                    name="password"
                    placeholder={staticData.loadTerms(
                      state,
                      "VW_ISSUER_Authenticate_formPassword"
                    )}
                    type="password"
                    autoComplete="current-password"
                    onChange={formik.handleChange}
                    value={formik.values.password}
                    disabled={isBusy}
                  />
                  <div className="text-danger" style={{ fontWeight: "bold" }}>
                    {formik.errors.password ? formik.errors.password : null}
                  </div>
                </FormGroup>
                <Button
                  block
                  className="btn-round"
                  color="danger"
                  type="submit"
                  disabled={isBusy}
                >
                  {isBusy
                    ? "..."
                    : staticData.loadTerms(
                        state,
                        "VW_ISSUER_Authenticate_BtnLogin"
                      )}
                </Button>
              </Form>
              <div className="forgot">
                <Link to={withLang(state, issuerUrls.FORGOT_PASSWORD_URL)}>
                  <Button className="btn-link" color="danger" type="button">
                    {staticData.loadTerms(
                      state,
                      "VW_ISSUER_Authenticate_BtnForgotPassword"
                    )}
                  </Button>
                </Link>
              </div>
            </Card>
          </Col>
        </Row>
      </Container>

      <div className="react-notification-alert-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
    </React.Fragment>
  );
};

export default Authenticate;
